import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Form, Input, Label, Button, Card, CardBody, FormFeedback } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import FeatherIcon from "feather-icons-react";
import loginImg from "../../assets/images/admin/login.svg";
import { post } from "../../components/helpers/api_helper";
import { toast } from 'react-toastify';
import MetaTags from 'react-meta-tags';
import { useAuth } from "../../store/auth";

const PageLogin = () => {
    const { storeTokenInLS } = useAuth();

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Enter a valid email").required("Email is required"),
            password: Yup.string().min(4, 'Password must be at least 4 characters').required("Please Enter Password")
        }),
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const response = await post('/admin/login', values);
                if (response?.ok) {
                    storeTokenInLS(response.token);
                    toast.success("Login successful. Redirecting...");
                    // Modernization: Use navigate for a smoother, client-side redirect
                    setTimeout(() => window.location.replace("/admin-dashboard"), 1000);
                }
            } catch (error) {
                const errorMessage = error?.message || "An unknown error occurred.";
                toast.error(errorMessage);
                // Optionally set form-level errors from the server
                if (error?.errors) {
                    setErrors(error.errors);
                }
            } finally {
                setSubmitting(false);
            }
        }
    });

    return (
        <React.Fragment>
            <MetaTags>
                <title>Admin Login | Subul Contracting</title>
            </MetaTags>
            <div className="back-to-home">
                <Link to="/" className="back-button btn btn-icon btn-primary">
                    <i><FeatherIcon icon="arrow-left" className="icons" /></i>
                </Link>
            </div>
            {/* Modernization: Using a clean, light background for a professional feel */}
            <section className="bg-light d-flex align-items-center min-vh-100">
                <Container>
                    <Row className="justify-content-center align-items-center">
                        <Col lg={7} md={6} className="d-none d-md-block">
                            <div className="me-lg-5 text-center">
                                <img src={loginImg} className="img-fluid" alt="Login Illustration" />
                            </div>
                        </Col>
                        <Col lg={5} md={6} className="d-flex justify-content-center">
                            <div className="w-100" style={{ maxWidth: '400px' }}>
                            {/* Modernization: Consistent rounded corners and a more prominent shadow */}
                            <Card className="login-page shadow-lg rounded-3 border-0">
                                <CardBody className="p-4 p-md-5">
                                    <div className="text-center">
                                        <h4 className="mb-3">Welcome Back!</h4>
                                        <p className="text-muted">Sign in to continue to the admin panel.</p>
                                    </div>
                                    <Form
                                        onSubmit={validation.handleSubmit}
                                        className="login-form mt-4"
                                    >
                                        <Row>
                                            <Col lg={12}>
                                                <div className="mb-3">
                                                    <Label className="form-label" htmlFor="email">Your Email <span className="text-danger">*</span></Label>
                                                    <div className="form-icon position-relative">
                                                        <FeatherIcon icon="user" className="fea icon-sm icons" />
                                                        <Input
                                                            type="email"
                                                            className="form-control ps-5"
                                                            name="email"
                                                            id="email"
                                                            placeholder="name@example.com"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.email}
                                                            invalid={!!(validation.touched.email && validation.errors.email)}
                                                        />
                                                        <FormFeedback>{validation.errors.email}</FormFeedback>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={12}>
                                                <div className="mb-3">
                                                    <Label className="form-label" htmlFor="password">Password <span className="text-danger">*</span></Label>
                                                    <div className="form-icon position-relative">
                                                        <FeatherIcon icon="key" className="fea icon-sm icons" />
                                                        <Input
                                                            type="password"
                                                            className="form-control ps-5"
                                                            name="password"
                                                            id="password"
                                                            placeholder="Password"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.password}
                                                            invalid={!!(validation.touched.password && validation.errors.password)}
                                                        />
                                                        <FormFeedback>{validation.errors.password}</FormFeedback>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={12}>
                                                <div className="d-flex justify-content-between">
                                                    <div className="mb-3">
                                                        <div className="form-check">
                                                            <Input type="checkbox" className="form-check-input" id="flexCheckDefault" />
                                                            <Label className="form-check-label" htmlFor="flexCheckDefault">Remember me</Label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col lg={12} className="mb-0">
                                                <div className="d-grid">
                                                    {/* Modernization: Consistent button styling */}
                                                    <Button color="primary" type="submit" className="btn-lg rounded-pill" disabled={validation.isSubmitting}>
                                                        {validation.isSubmitting ? "Signing in..." : "Sign in"}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </CardBody>
                            </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
}
export default PageLogin;